/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

interface NavbarConfig {
  width?: number;
  breakpoint?: string;
  collapsed?: { mobile?: boolean; desktop?: boolean };
}

interface HeaderConfig {
  height?: number;
}

export type AppShellProps = {
  children: React.ReactNode;
  navbar?: NavbarConfig | React.ReactNode;
  header?: HeaderConfig | React.ReactNode;
  footer?: React.ReactNode;
  aside?: React.ReactNode;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  className?: string;
};

const paddingClasses: Record<string, string> = {
  xs: 'p-1',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

/**
 * Resolve padding prop to a Tailwind class
 */
function resolvePadding(padding: AppShellProps['padding']): { className: string; style?: React.CSSProperties } {
  if (padding === 0) return { className: 'p-0' };
  if (typeof padding === 'number') return { className: '', style: { padding: `${padding}px` } };
  return { className: paddingClasses[padding ?? 'md'] ?? paddingClasses.md };
}

/**
 * AppShell wrapper for application layout with header, navbar, main, and footer zones.
 * Supports both Mantine-style config objects and compound component children.
 */
const AppShellRoot: React.FC<AppShellProps> = ({
  children,
  padding = 'md',
  className,
}) => {
  const resolved = resolvePadding(padding);
  return (
    <div className={cn('flex flex-col h-full', resolved.className, className)} style={resolved.style}>
      {children}
    </div>
  );
};

const Navbar: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <nav className={cn('flex-shrink-0', className)}>{children}</nav>
);

const Main: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <main className={cn('flex-1 min-w-0 min-h-0 flex flex-col', className)}>{children}</main>
);

const Header: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <header className={cn('flex-shrink-0', className)}>{children}</header>
);

const Footer: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <footer className={cn('flex-shrink-0', className)}>{children}</footer>
);

/**
 * AppShell with compound sub-components for layout zones
 */
export const AppShell = Object.assign(AppShellRoot, {
  Navbar,
  Main,
  Header,
  Footer,
});

export default AppShell;
